import React, { useState } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { orbitType } from '../../styles';
import { formatSupportDate, formatSupportMoney } from '../supportFormat';
import {
  ContractSummaryBlock,
  FeatureSubscriptionsBlock,
  MultiAnswerBlock,
  OutOfScopeBlock,
  SupportOption,
  SupportTicketDraftBlock
} from '../types';
import {
  AnswerCard,
  CtaRow,
  Intro,
  Pill,
  ResultRow,
  RowBody,
  RowTitle,
  Stack,
  SupportActionButton,
  sp
} from './supportStyles';

const T = `${TOrbit}.support`;

let ticketDraftCount = 0;

export const FeatureSubscriptions: React.FC<{ block: FeatureSubscriptionsBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Stack $gap={10}>
      <Intro>{block.intro}</Intro>
      <AnswerCard $gap={12}>
        <Stack $gap={8}>
          {block.features.map(feature => (
            <ResultRow $gap={4} key={feature.name} data-testid="orbit-support-feature">
              {feature.subscribed ? null : (
                <Pill $color={sp.warning} $outlined>
                  {t(`${T}.notSubscribed`)}
                </Pill>
              )}
              <RowTitle>{feature.name}</RowTitle>
              <RowBody>{feature.description}</RowBody>
            </ResultRow>
          ))}
        </Stack>
        <CtaRow>
          {block.actions.map(action => (
            <SupportActionButton key={action.id} action={action} />
          ))}
        </CtaRow>
      </AnswerCard>
    </Stack>
  );
};

const TicketCard = styled.div`
  border: 1px solid ${sp.border};
  border-radius: 12px;
  background: ${sp.inset};
  overflow: hidden;
`;

const TicketHead = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${sp.border};
  background: ${sp.bubble};
  color: ${sp.text};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

const TicketBody = styled.div`
  padding: 6px 12px;
`;

const TicketLine = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 7px 0 8px;
  ${orbitType.containerBody}

  & + & {
    border-top: 1px solid ${sp.border};
  }

  > :first-child {
    flex: 0 0 105px;
    color: ${sp.muted};
  }

  > :last-child {
    flex: 1 1 auto;
    min-width: 0;
    margin-left: 8px;
  }

  > span:last-child {
    color: ${sp.text};
    font-weight: 700;
  }
`;

const editable = `
  width: 100%;
  padding: 8px 9px;
  border: 1px solid ${sp.border};
  border-radius: 7px;
  background: ${sp.bubble};
  color: ${sp.text};
  font: inherit;
  ${orbitType.containerBody}
  font-weight: 400;
  resize: vertical;
`;

const EditableInput = styled.textarea`
  ${editable}
`;

const InlineSelect = styled.select`
  max-width: 100%;
  padding: 0 4px 0 0;
  border: none;
  background: transparent;
  color: ${sp.text};
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  option {
    background: ${sp.bubble};
    color: ${sp.text};
  }
`;

const ResultBanner = styled.p<{ $error: boolean }>`
  margin: 0;
  padding: 10px 12px;
  border: 1px solid ${({ $error }) => ($error ? sp.danger : sp.success)};
  border-radius: 10px;
  background: ${({ $error }) => ($error ? sp.danger : sp.success)}24;
  color: ${sp.text};
  ${orbitType.containerBody}

  > strong {
    color: ${({ $error }) => ($error ? sp.danger : sp.success)};
    font-weight: 700;
  }
`;

const FreeText = styled.input`
  width: 100%;
  padding: 6px 9px;
  border: 1px solid ${sp.border};
  border-radius: 7px;
  background: ${sp.bubble};
  color: ${sp.text};
  font: inherit;
  font-weight: 400;
`;

const labelOf = (options: SupportOption[] | undefined, value: string): string =>
  options?.find(option => option.value === value)?.label ?? value;

/**
 * FR §4.18: the card is the review step. Prefilled values come from the response and show
 * read-only; only the missing required fields (Issue Description, Issue Type / Category, Domain,
 * Environment) are collected. No severity or P1/P2/P3 is asked for or assigned. "Create support
 * ticket" is the explicit confirmation; success shows only Zendesk's own ID and status (§4.19).
 */
export const SupportTicketDraft: React.FC<{ block: SupportTicketDraftBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const { draft, status } = block;
  const options = block.options ?? {};
  const [environment, setEnvironment] = useState(draft.environment ?? '');
  const [domain, setDomain] = useState(draft.domain ?? '');
  const [issueType, setIssueType] = useState(draft.issue_type ?? '');
  const [description, setDescription] = useState(draft.description ?? '');
  const [prefix] = useState(() => {
    ticketDraftCount += 1;
    return `orbit-support-ticket-${ticketDraftCount}`;
  });
  const complete =
    environment !== '' && domain !== '' && issueType !== '' && description.trim() !== '';
  const locked = status !== 'draft';

  // A missing field is a select when the response supplies its choices, free text otherwise.
  const choose = (
    id: string,
    label: string,
    value: string,
    options: SupportOption[] | undefined,
    onChange: (next: string) => void
  ) => (
    <TicketLine>
      <label htmlFor={id}>{label}</label>
      <span>
        {options !== undefined && options.length > 0 ? (
          <InlineSelect
            id={id}
            value={value}
            disabled={locked}
            onChange={event => onChange(event.target.value)}
          >
            <option value="">{t(`${T}.ticket.select`)}</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </InlineSelect>
        ) : (
          <FreeText
            id={id}
            value={value}
            readOnly={locked}
            onChange={event => onChange(event.target.value)}
          />
        )}
      </span>
    </TicketLine>
  );

  const fixed = (label: string, value: string) => (
    <TicketLine>
      <span>{label}</span>
      <span>{value}</span>
    </TicketLine>
  );

  return (
    <Stack $gap={10}>
      <Intro>{block.intro}</Intro>
      <TicketCard>
        <TicketHead>{block.title}</TicketHead>
        <TicketBody>
          {draft.environment !== null
            ? fixed(t(`${T}.ticket.environment`), labelOf(options.environment, draft.environment))
            : choose(
                `${prefix}-environment`,
                t(`${T}.ticket.environment`),
                environment,
                options.environment,
                setEnvironment
              )}
          {draft.domain !== null
            ? fixed(t(`${T}.ticket.domain`), labelOf(options.domain, draft.domain))
            : choose(
                `${prefix}-domain`,
                t(`${T}.ticket.domain`),
                domain,
                options.domain,
                setDomain
              )}
          {draft.issue_type !== null
            ? fixed(t(`${T}.ticket.issueType`), labelOf(options.issue_type, draft.issue_type))
            : choose(
                `${prefix}-issue-type`,
                t(`${T}.ticket.issueType`),
                issueType,
                options.issue_type,
                setIssueType
              )}
          {draft.area ? fixed(t(`${T}.ticket.area`), draft.area) : null}
          {fixed(t(`${T}.ticket.tenant`), draft.tenant)}
          {draft.detected_issue
            ? fixed(t(`${T}.ticket.detectedIssue`), draft.detected_issue)
            : null}
          {draft.description !== null ? (
            fixed(t(`${T}.ticket.description`), draft.description)
          ) : (
            <TicketLine>
              <label htmlFor={`${prefix}-description`}>{t(`${T}.ticket.description`)}</label>
              <EditableInput
                id={`${prefix}-description`}
                rows={3}
                value={description}
                readOnly={locked}
                onChange={event => setDescription(event.target.value)}
              />
            </TicketLine>
          )}
          {draft.evidence ? fixed(t(`${T}.ticket.evidence`), draft.evidence) : null}
        </TicketBody>
      </TicketCard>
      {status === 'created' && block.result ? (
        <ResultBanner role="status" $error={false}>
          <strong>{t(`${T}.ticket.created`)}</strong>{' '}
          {t(`${T}.ticket.createdDetail`, {
            id: block.result.ticket_id,
            status: block.result.status
          })}
        </ResultBanner>
      ) : null}
      {status === 'failed' ? (
        <ResultBanner role="alert" $error>
          <strong>{t(`${T}.ticket.failed`)}</strong> {block.error ?? ''}
        </ResultBanner>
      ) : null}
      {status === 'created' ? null : (
        <CtaRow>
          {block.actions.map(action => {
            if (action.id !== 'create_ticket') {
              return locked ? null : <SupportActionButton key={action.id} action={action} />;
            }
            if (status === 'submitting') {
              return (
                <SupportActionButton
                  key={action.id}
                  action={{ ...action, label: t(`${T}.ticket.submitting`) }}
                  disabled
                />
              );
            }
            return (
              <SupportActionButton
                key={action.id}
                action={action}
                disabled={status === 'draft' && !complete}
              />
            );
          })}
        </CtaRow>
      )}
    </Stack>
  );
};

const MutedBody = styled.p`
  margin: 0;
  color: ${sp.muted};
  ${orbitType.containerBody}
`;

export const OutOfScope: React.FC<{ block: OutOfScopeBlock }> = ({ block }) => (
  <Stack $gap={10}>
    <Intro>{block.intro}</Intro>
    <AnswerCard $gap={8}>
      <Pill $color={sp.danger} $outlined>
        {block.badge}
      </Pill>
      <MutedBody>{block.body}</MutedBody>
      <CtaRow>
        {block.actions.map(action => (
          <SupportActionButton key={action.id} action={action} />
        ))}
      </CtaRow>
    </AnswerCard>
  </Stack>
);

const AnswerNumber = styled.span`
  color: ${sp.muted};
  ${orbitType.helping}
  font-weight: 700;
`;

export const MultiAnswer: React.FC<{ block: MultiAnswerBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Stack $gap={10}>
      <Intro>{block.intro}</Intro>
      <Stack $gap={8}>
        {block.answers.map((answer, index) => (
          <ResultRow $gap={4} key={answer.title}>
            <AnswerNumber>
              {t(`${T}.answerNumber`, { index: index + 1, total: block.answers.length })}
            </AnswerNumber>
            <RowTitle>{answer.title}</RowTitle>
            <RowBody>{answer.body}</RowBody>
            {answer.actions.map(action => (
              <SupportActionButton key={action.id} action={action} />
            ))}
          </ResultRow>
        ))}
      </Stack>
    </Stack>
  );
};

const ContractRow = styled.div`
  display: flex;
  padding: 8px 0;
  ${orbitType.containerBody}

  & + & {
    border-top: 1px solid ${sp.border};
  }

  > :first-child {
    flex: 0 0 120px;
    color: ${sp.muted};
  }

  > :last-child {
    margin-left: 10px;
    color: ${sp.text};
    font-weight: 700;
  }
`;

export const ContractSummary: React.FC<{ block: ContractSummaryBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Stack $gap={10}>
      <Intro>{block.intro}</Intro>
      <AnswerCard $gap={0}>
        <ContractRow>
          <span>{t(`${T}.contract.value`)}</span>
          <span>{formatSupportMoney(block.contract_value)}</span>
        </ContractRow>
        <ContractRow>
          <span>{t(`${T}.contract.duration`)}</span>
          <span>
            {t(`${T}.contract.range`, {
              start: formatSupportDate(block.start_date),
              end: formatSupportDate(block.end_date)
            })}
          </span>
        </ContractRow>
        <CtaRow>
          {block.actions.map(action => (
            <SupportActionButton key={action.id} action={action} />
          ))}
        </CtaRow>
      </AnswerCard>
    </Stack>
  );
};
